import { useCallback, useEffect } from "react";
import { useState } from "react";
import { useAtom } from "jotai";
import { currentSpaceAtom } from "../jotai/atoms";
import { useRoom } from "@roomservice/react";

export function useRoomClient() {
  const [currentSpace] = useAtom(currentSpaceAtom);
  return useRoom(currentSpace!.id);
}

// Given a set of keys for maps of the same type,
// return state tracking those maps
export function useMapFamily<T>(keys: string[]) {
  const [maps, setMaps] = useState<Record<string, T>>({});
  const room = useRoomClient();

  const updateMaps = useCallback(
    function (key: string, value: T) {
      setMaps((old) => {
        return {
          ...old,
          [key]: value,
        };
      });
    },
    [setMaps]
  );

  useEffect(() => {
    if (!room) return;
    const listeners: any[] = [];
    const newMaps: Record<string, T> = {}; // Reset maps to get rid of stale key values
    keys.forEach((key) => {
      const client = room.map<T>(key);
      newMaps[key] = client.toObject();
      listeners.push(
        room.subscribe(client, (map: T) => {
          updateMaps(key, map);
        })
      );
    });
    setMaps(newMaps);
    return () => {
      listeners.forEach((l) => room.unsubscribe(l));
    };
  }, [room, updateMaps, keys, setMaps]);

  return maps;
}

// Attach a callback to a presence subscription
export function usePresenceCallback(key: string, callback: (msg: any) => void) {
  const room = useRoomClient();

  useEffect(() => {
    if (!room) return;
    const presence = room.presence(key);
    callback(presence.getAll());
    const listener = room.subscribe(presence, callback);

    return () => room.unsubscribe(listener);
  }, [room, key, callback]);
}
